import AOS from "aos";
import "aos/dist/aos.css";
import {
  Building2,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // API call would go here
      const response = await fetch("https://oethical-website-backend.vercel.app/api/contactUser/byId", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        throw new Error("Failed to submit");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Office Address",
      description: "D2/258, Narmadapuram Rd, opposite Aashima Mall, Danish Nagar, Bagmugaliya",
      detail: "Bhopal, Madhya Pradesh 462026, India",
      link: "https://maps.google.com/?q=D2/258+Narmadapuram+Rd+Bhopal",
      gradient: "from-blue-500/20 to-cyan-500/20",
    },
    {
      icon: Mail,
      title: "Email Us",
      description: "Get in touch via email",
      detail: "admin@oethical.com",
      link: "mailto:admin@oethical.com",
      gradient: "from-purple-500/20 to-pink-500/20",
    },
    {
      icon: Phone,
      title: "Call Us",
      description: "Speak with our team",
      detail: "+91 9532912517",
      link: "tel:+919532912517",
      gradient: "from-green-500/20 to-emerald-500/20",
    },
    {
      icon: Clock,
      title: "Business Hours",
      description: "We're here to help",
      detail: "Mon - Sat: 09:00 AM - 7:00 PM",
      link: null,
      gradient: "from-orange-500/20 to-red-500/20",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 lg:py-24 bg-gradient-to-br from-background via-muted/20 to-background border-b overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="container relative z-10">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <div 
              className="inline-block"
              data-aos="fade-down"
              data-aos-delay="100"
            >
              <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                Get In Touch
              </span>
            </div>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Contact{" "}
              <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Us
              </span>
            </h1>
            <p 
              className="text-lg md:text-xl text-muted-foreground leading-relaxed"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              Have a question or want to discuss your project? We're here to help.
              Reach out to us and let's build something amazing together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  className={cn(
                    "border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group",
                    "bg-gradient-to-br",
                    info.gradient
                  )}
                >
                  <CardHeader>
                    <div className="h-14 w-14 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-lg mb-2">{info.title}</CardTitle>
                    <CardDescription className="text-sm">{info.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {info.link ? (
                      <a
                        href={info.link}
                        target={info.link.startsWith("http") ? "_blank" : undefined}
                        rel={info.link.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="text-base font-medium text-foreground hover:text-primary transition-colors block"
                      >
                        {info.detail}
                      </a>
                    ) : (
                      <p className="text-base font-medium text-foreground">{info.detail}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Side - Info */}
            <div className="space-y-8" data-aos="fade-right" data-aos-delay="100">
              <div>
                <div className="inline-block mb-4">
                  <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                    Send Message
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Drop Us A{" "}
                  <span className="text-primary">Line</span>
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Fill out the form below and our team will get back to you within 24 hours.
                  We're committed to providing exceptional service and support.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Quick Response</h3>
                    <p className="text-muted-foreground text-sm">
                      We typically respond within 24 hours during business days.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Expert Consultation</h3>
                    <p className="text-muted-foreground text-sm">
                      Get free consultation from our IT experts on your project needs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <Card 
              className="border-border shadow-xl"
              data-aos="fade-left"
              data-aos-delay="200"
            >
              <CardHeader>
                <CardTitle className="text-2xl">Contact Form</CardTitle>
                <CardDescription>
                  Please fill in all the required fields below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                      <CheckCircle2 className="h-8 w-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">
                      Message Sent Successfully!
                    </h3>
                    <p className="text-muted-foreground">
                      Thank you for contacting us. We'll get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm font-medium">
                          Phone Number
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+91 1234567890"
                          value={formData.phone}
                          onChange={handleChange}
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="subject" className="text-sm font-medium">
                          Subject <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          placeholder="Project Inquiry"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-medium">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        placeholder="Tell us about your project or inquiry..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className={cn(
                          "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                          "disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                        )}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full group"
                      disabled={loading}
                    >
                      {loading ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-20 bg-background border-t">
        <div className="container">
          <div 
            className="text-center mb-8"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Find Us on{" "}
              <span className="text-primary">Map</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Visit our office or get directions to our location.
            </p>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4" />
          </div>

          <Card 
            className="overflow-hidden border-border shadow-xl"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <div className="relative w-full h-[450px] md:h-[500px]">
              <iframe
                className="w-full h-full border-0"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14655.161141291473!2d77.4569984!3d23.1800832!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c43c7222a1999%3A0xc92103abeac5c2d5!2sOethical+Technology!5e0!3m2!1sen!2sus!4v1688534558332!5m2!1sen!2sus"
                allowFullScreen=""
                loading="lazy"
                title="Oethical Technology Location"
              />
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
